// src/lrp/LrpPushSignalView.jsx — the LRP Push Signal, admin-only.
//
// Renders what src/lrp/lrpPush.js decided. It contains NO economics of its own:
// every number here was computed by the engine and stored by api/_cattle-cog.js,
// so the screen and the table cannot disagree. That is deliberate — v1's UI
// derived "best margin" and "endorsements in profit" itself, which is how the
// page came to present a per-cell count as though it were a list of
// opportunities.
//
// -- WHAT THIS PAGE IS FOR, IN ONE LINE ------------------------------------
//
// It identifies who is worth CALLING. It is not a quote and it is not advice to
// bind. The handoff is printed on the page because the page is standardized and
// a real producer's costs are not:
//
//     Push Signal finds the opportunity
//       -> agent calls the producer
//       -> the producer's real numbers go into the LRP Profit Calculator
//       -> the agent runs the real quote
//
// -- LANGUAGE RULE, ENFORCED BY tools/check-lrp-push.js ---------------------
//
// "guaranteed", "lockable" and "lock a profit" are FORBIDDEN here. v1 printed
// "Best lockable margin +$47.29/cwt" beside a figure that had no premium and no
// basis in it. A modeled number carrying named assumptions is not a promise, and
// the words on the page are the only thing that says so to somebody skimming.
//
// The data fetcher is INJECTED rather than imported, so this module carries no
// dependency on App.jsx internals and the caller's existing Supabase session
// (and therefore its RLS path) is unchanged.

import React, { useState, useEffect, useMemo } from "react";
import { resolveBasis } from "./lrpPush";

// The basis was MEASURED on NY feeder steers only. Fed rows carry it BORROWED,
// and the card must say so - "measured" on a fed card is the claim the engine refuses.
function basisProvenanceNote(key, seg) {
  const b = resolveBasis(key, seg);
  if (b && b.provenance === "borrowed") return " · borrowed from NY feeder - fed basis not measured";
  return " · measured, not observed";
}

const INK = "#1a2e1a", MID = "#2d4a2d", MUTED = "#60735f", LINE = "#e0eae0";

const DECISION_META = {
  PUSH_NOW: { label: "PUSH NOW", bg: "#e6f4e6", fg: "#1a6e1a", dot: "🟢",
    gloss: "Robust protected economics AND today's net offer is historically strong or deteriorating from a strong level." },
  CONTACT: { label: "CONTACT", bg: "#eaf3ea", fg: "#2d6e2d", dot: "🟢",
    gloss: "Standardized economics protect a profitable margin under conservative costs. Call likely-eligible producers and replace these assumptions with their real numbers." },
  WATCH: { label: "WATCH", bg: "#fff7e6", fg: "#9a6a00", dot: "🟡",
    gloss: "Near the threshold, or improving but not yet clear of it. Nothing to act on today." },
  NO_PUSH: { label: "NO PUSH", bg: "#f0f0ee", fg: "#6a6a5a", dot: "⚪",
    gloss: "Conservative protected economics do not clear the bar." },
  NO_READ: { label: "NO READ", bg: "#f3eef0", fg: "#8a5a5a", dot: "⚫",
    gloss: "Inputs are stale, incomplete or invalid, or no endorsement matches the modeled production timeline. The signal is not being withheld — it cannot be computed." },
};

const DQ_META = {
  strong: { label: "Strong", fg: "#1a6e1a" },
  limited: { label: "Limited", fg: "#8a6a00" },
  fallback: { label: "Fallback", fg: "#9a5a00" },
  stale: { label: "Stale", fg: "#b0563a" },
  insufficient: { label: "Insufficient", fg: "#b0563a" },
};

const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const d$ = (v, dp) => { const x = n(v); return x == null ? "—" : (x < 0 ? "-$" : "$") + Math.abs(x).toFixed(dp == null ? 2 : dp); };
const sd$ = (v, dp) => { const x = n(v); return x == null ? "—" : (x >= 0 ? "+" : "−") + "$" + Math.abs(x).toFixed(dp == null ? 2 : dp); };
const dt = (v) => { if (!v) return "—"; const t = new Date(String(v).slice(0, 10) + "T12:00:00Z"); return isNaN(t) ? String(v) : t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); };

export default function LrpPushSignalView({ isMobile, sbFetch }) {
  const [rows, setRows] = useState([]);
  const [snapDate, setSnapDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [showAll, setShowAll] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const latest = await sbFetch("cattle_breakeven", { select: "snapshot_date", order: "snapshot_date.desc", filters: ["limit=1"] });
        const d = latest && latest[0] && latest[0].snapshot_date;
        if (!d) { if (alive) { setRows([]); setLoading(false); } return; }
        const data = await sbFetch("cattle_breakeven", { select: "*", filters: [`snapshot_date=eq.${d}`], order: "segment,end_date,coverage_level" });
        if (alive) { setSnapDate(d); setRows(data || []); }
      } catch (e) { if (alive) setError(e.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [sbFetch]);

  const SEG_META = {
    feeder: { label: "Feeder sellers", sub: "cow-calf / backgrounder · standardized 525 lb → 800 lb programme" },
    fed: { label: "Fed cattle", sub: "feedlot placing cattle now · standardized 800 lb → 1,500 lb programme" },
  };

  const segs = useMemo(() => {
    const out = {};
    for (const seg of ["feeder", "fed"]) {
      const r = rows.filter((x) => x.segment === seg);
      if (!r.length) continue;
      const a = r[0];
      // LEGACY ROWS. Anything written before 2026-08-28 carries no model_version
      // and its margin_cwt is the v1 quantity — coverage price minus breakeven,
      // with no premium and no basis in it. Those two numbers are not comparable
      // and must never be shown side by side as though they were.
      const legacy = !a.model_version;
      const eligible = r.filter((x) => x.eligible);
      const byEnd = {};
      for (const x of eligible) {
        const k = x.end_date;
        if (!byEnd[k] || n(x.margin_hd) > n(byEnd[k].margin_hd)) byEnd[k] = x;
      }
      const best = Object.values(byEnd).sort((p, q) => String(p.end_date).localeCompare(String(q.end_date)));
      const ineligible = r.filter((x) => !x.eligible);
      const nearMiss = [];
      const seenMiss = {};
      for (const x of ineligible) {
        if (seenMiss[x.end_date]) continue;
        seenMiss[x.end_date] = 1; nearMiss.push(x);
      }
      out[seg] = {
        legacy, decision: a.decision || null, why: a.decision_why || [],
        dq: a.data_quality || null, dqReasons: a.data_quality_reasons || [],
        entry: a.entry_quality, entryPctile: a.entry_pctile,
        rateDate: a.rate_date, cashWeek: a.cash_week_ending, cashSource: a.cash_source,
        cashQuality: a.cash_quality, feedMonth: a.feed_month, inCwt: n(a.in_animal_cwt),
        modelVersion: a.model_version,
        adg: n(a.assumed_adg), readyDate: a.ready_date, daysOnFeed: n(a.days_on_feed),
        basis: n(a.basis_cwt), basisScenario: a.basis_scenario,
        corn: n(a.corn_price), hay: n(a.hay_price), soymeal: n(a.soymeal_price),
        rows: best, nearMiss: nearMiss.sort((p, q) => String(p.end_date).localeCompare(String(q.end_date))),
        eligibleEnds: best.length,
        profitableStd: best.filter((x) => n(x.margin_hd_standard) > 0).length,
        profitableCons: best.filter((x) => n(x.margin_hd_conservative) > 0).length,
        bestRow: best.reduce((acc, x) => (acc == null || n(x.margin_hd) > n(acc.margin_hd) ? x : acc), null),
      };
    }
    return out;
  }, [rows]);

  const mColor = (m) => { const v = n(m); return v == null ? MUTED : v >= 50 ? "#1a6e1a" : v > 0 ? "#4a6e4a" : v > -100 ? "#9a7d0a" : "#b0563a"; };

  const card = (label, value, sub, accent) => (
    <div style={{ background: "#fff", border: "1px solid " + LINE, borderRadius: 10, padding: isMobile ? "10px 12px" : "13px 15px", flex: 1, minWidth: isMobile ? 130 : 155 }}>
      <div style={{ fontSize: 10.5, color: MUTED, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: isMobile ? 18 : 21, fontWeight: 800, color: accent || INK, fontFamily: "'Lora','Georgia',serif", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2, lineHeight: 1.35 }}>{sub}</div>}
    </div>
  );

  const SegBlock = ({ seg }) => {
    const d = segs[seg]; if (!d) return null;
    const m = SEG_META[seg];
    const dec = DECISION_META[d.decision] || DECISION_META.NO_READ;
    const dq = DQ_META[d.dq] || { label: d.dq || "unknown", fg: MUTED };
    const open = !!showAll[seg];

    if (d.legacy) {
      return (
        <div style={{ marginBottom: 26, background: "#fff7e6", border: "1px solid #f0d9a0", borderRadius: 10, padding: 16 }}>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 16, fontWeight: 700, color: INK }}>{m.label}</div>
          <div style={{ fontSize: 13, color: "#8a6a2a", marginTop: 6, lineHeight: 1.5, maxWidth: 760 }}>
            This snapshot predates the {new Date("2026-08-28T12:00:00Z").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} rebuild. Its stored margin is
            the earlier quantity — coverage price minus breakeven, with <b>no producer premium and no local basis in it</b> — so it is not
            comparable to the figures this page now reports and is deliberately not shown. Re-run the engine to populate a current snapshot.
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 30 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
          <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 17, fontWeight: 700, color: INK }}>{m.label}</div>
          <div style={{ fontSize: 12, color: MUTED }}>{m.sub}</div>
          <div style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 800, padding: "4px 12px", borderRadius: 6, background: dec.bg, color: dec.fg, whiteSpace: "nowrap" }}>
            {dec.dot} {dec.label}
          </div>
        </div>

        {/* the decision and its reason lead the block — never a number alone */}
        <div style={{ background: "#fafdfa", border: "1px solid " + LINE, borderRadius: 10, padding: "11px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, color: MID, lineHeight: 1.55 }}>{dec.gloss}</div>
          {!!(d.why || []).length && (
            <ul style={{ margin: "7px 0 0", paddingLeft: 18, fontSize: 12.5, color: "#42563f", lineHeight: 1.55 }}>
              {d.why.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>

        <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 12 }}>
          {card("Best conservative margin", d.bestRow ? sd$(d.bestRow.margin_hd_conservative, 0) + "/hd" : "—",
            d.bestRow ? sd$(d.bestRow.margin_cwt) + "/cwt · modeled, protected" : "no eligible endorsement", mColor(d.bestRow && d.bestRow.margin_hd_conservative))}
          {card("Protected cash floor", d.bestRow ? d$(d.bestRow.protected_cash_floor_cwt) + "/cwt" : "—",
            d.bestRow ? "coverage " + d$(d.bestRow.lrp_floor_cwt) + " − prem " + d$(d.bestRow.producer_premium_cwt) + " + basis " + sd$(d.bestRow.basis_cwt) : null)}
          {card("Producer premium", d.bestRow ? d$(d.bestRow.producer_premium_cwt) + "/cwt" : "—",
            d.bestRow ? "gross " + d$(d.bestRow.lrp_cost_cwt) + " × " + Math.round(n(d.bestRow.subsidy_share) * 100) + "% producer share" : null, "#7a4a00")}
          {card("Expected basis", d.basis == null ? "—" : sd$(d.basis) + "/cwt",
            d.basisScenario ? d.basisScenario.replace(/_/g, " ") + basisProvenanceNote(d.basisScenario, seg) : null, "#7a4a00")}
          {card("Realistic end dates", String(d.eligibleEnds),
            d.eligibleEnds ? d.profitableCons + " profitable (conservative), " + d.profitableStd + " (standard)" : "none match the timeline",
            d.eligibleEnds ? INK : MUTED)}
          {card("Data quality", dq.label, d.cashQuality ? "cash read: " + d.cashQuality : null, dq.fg)}
        </div>

        {/* provenance, on the face of the page — v1 showed a snapshot date that
            was not the rate date and printed neither source */}
        <div style={{ fontSize: 11, color: "#677167", marginBottom: 10, lineHeight: 1.6 }}>
          LRP rates <b>{dt(d.rateDate)}</b> · NY cash week <b>{dt(d.cashWeek)}</b> ({d.cashSource === "eev_fallback" ? "EEV fallback — not local cash" : "NY auction"}, {d$(d.inCwt)}/cwt at purchase weight)
          {" "}· feed <b>{d.feedMonth || "—"}</b> (corn {d$(d.corn)}/bu, hay {d$(d.hay, 0)}/ton, meal {d$(d.soymeal, 0)}/ton)
          {" "}· conservative ADG {d.adg == null ? "—" : d.adg.toFixed(1)} lb/d → market-ready <b>{dt(d.readyDate)}</b>
          {d.modelVersion && d.modelVersion.engine ? " · model " + d.modelVersion.engine : ""}
        </div>

        {!!(d.dqReasons || []).length && (
          <div style={{ fontSize: 11.5, color: "#8a6a2a", background: "#fffbf0", border: "1px solid #f0e3c0", borderRadius: 8, padding: "8px 11px", marginBottom: 12, lineHeight: 1.55 }}>
            <b>What limits this read:</b>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{d.dqReasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}

        {d.rows.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid " + LINE, borderRadius: 10, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: isMobile ? 11.5 : 12.5 }}>
              <thead><tr style={{ background: "#f2f6f2", textAlign: "left" }}>
                {["End date", "Timing fit", "Cov", "Net index floor", "Basis", "Protected cash floor", "Standard", "Conservative"].map((h) => (
                  <th key={h} style={{ padding: "8px 9px", fontWeight: 700, color: MID, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {d.rows.map((e, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #eef3ee" }}>
                    <td style={{ padding: "9px", whiteSpace: "nowrap", fontWeight: 700 }}>{dt(e.end_date)}<span style={{ fontWeight: 400, color: MUTED }}> · {e.endorsement_weeks} wk</span></td>
                    <td style={{ padding: "9px", color: MUTED, whiteSpace: "nowrap" }}>
                      {n(e.timing_offset_days) == null ? "—" : (n(e.timing_offset_days) >= 0 ? "ends " + n(e.timing_offset_days) + "d after ready" : Math.abs(n(e.timing_offset_days)) + "d before ready")}
                    </td>
                    <td style={{ padding: "9px", color: MUTED }}>{n(e.coverage_level) == null ? "—" : Math.round(n(e.coverage_level) * 100) + "%"}</td>
                    <td style={{ padding: "9px", whiteSpace: "nowrap" }}>{d$(e.net_index_floor_cwt)}
                      <span style={{ color: MUTED, fontSize: 11 }}> (−{d$(e.producer_premium_cwt)} prem)</span></td>
                    <td style={{ padding: "9px", color: "#7a4a00", whiteSpace: "nowrap" }}>{sd$(e.basis_cwt)}</td>
                    <td style={{ padding: "9px", fontWeight: 700, whiteSpace: "nowrap" }}>{d$(e.protected_cash_floor_cwt)}</td>
                    <td style={{ padding: "9px", color: mColor(e.margin_hd_standard), whiteSpace: "nowrap" }}>{sd$(e.margin_hd_standard, 0)}/hd</td>
                    <td style={{ padding: "9px", fontWeight: 800, color: mColor(e.margin_hd_conservative), whiteSpace: "nowrap" }}>{sd$(e.margin_hd_conservative, 0)}/hd</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* what was REJECTED and why — a page that silently omits what it
            excluded cannot be checked, and "no end date fits" is a finding */}
        {!!d.nearMiss.length && (
          <div style={{ marginTop: 9 }}>
            <button onClick={() => setShowAll((s) => ({ ...s, [seg]: !s[seg] }))}
              style={{ background: "none", border: "none", color: "#4a6e4a", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: 0 }}>
              {open ? "▾" : "▸"} {d.nearMiss.length} end date{d.nearMiss.length === 1 ? "" : "s"} excluded — the cattle are not market-ready in the window
            </button>
            {open && (
              <div style={{ marginTop: 6, fontSize: 12, color: MUTED, lineHeight: 1.7, paddingLeft: 14 }}>
                {d.nearMiss.map((x, i) => (
                  <div key={i}><b style={{ color: MID }}>{dt(x.end_date)}</b> ({x.endorsement_weeks} wk) — {x.ineligible_reason || "not eligible"}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const anySeg = segs.feeder || segs.fed;

  return (
    <div style={{ paddingTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#fff7e6", border: "1px solid #f0d9a0", borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#9a6a00", background: "#ffe9bf", borderRadius: 5, padding: "2px 8px", letterSpacing: 0.5 }}>ADMIN ONLY · SHADOW</span>
        <span style={{ fontSize: 12, color: "#8a6a2a" }}>Standardized modeled economics — not a quote, not a producer's own costs. Alerts are OFF while this is validated.</span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Lora','Georgia',serif", fontSize: 19, fontWeight: 700, color: INK }}>LRP Push Signal</div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 4, maxWidth: 800, lineHeight: 1.6 }}>
          Does current LRP coverage protect a genuinely profitable modeled margin — after the <b>producer premium</b>, after <b>expected NY basis</b>,
          and under <b>conservative</b> costs — strongly enough to be worth a call today? This page identifies who to phone. Replace these
          standardized assumptions with the producer's actual cattle, ration and expected sale basis in the <b>LRP Profit Calculator</b> before quoting.
        </div>
      </div>

      {loading && <div style={{ color: MUTED, padding: 20 }}>Loading signal…</div>}
      {error && <div style={{ color: "#c0392b", padding: 20 }}>Error: {error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div style={{ color: MUTED, padding: 20 }}>No snapshot yet — the engine computes daily at 05:30 UTC (or trigger <code>/api/market?fn=cattle-cog&compute=1</code>).</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: "#677167", marginBottom: 14 }}>snapshot {snapDate}</div>
          <SegBlock seg="feeder" />
          <SegBlock seg="fed" />

          <button onClick={() => setShowHow((v) => !v)} style={{ background: "none", border: "none", color: "#4a6e4a", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0 }}>
            {showHow ? "▾" : "▸"} How this is calculated
          </button>
          {showHow && (
            <div style={{ background: "#fafdfa", border: "1px solid " + LINE, borderRadius: 10, padding: 16, marginTop: 8, fontSize: 12.5, color: "#42563f", lineHeight: 1.65, maxWidth: 860 }}>
              <pre style={{ background: "#f2f6f2", borderRadius: 8, padding: 12, fontSize: 11.5, overflowX: "auto", lineHeight: 1.6, margin: "0 0 12px" }}>{
`purchase animal + feed (cost of gain) + yardage
  + vet/health + trucking + interest on cattle
  + interest on feed + death loss + marketing
= total modeled cost of ownership

total modeled cost / target sale cwt = breakeven $/cwt

LRP coverage price
  − producer premium   (gross premium x RMA subsidy share)
  + expected local basis
= protected cash floor

protected cash floor − breakeven = modeled protected margin`}</pre>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
                <li><b>LRP protects an index, not your cheque.</b> It settles against the national actual ending value; a NY seller realises
                  that index <i>plus local basis</i>. Basis is therefore the producer's own exposure and is shown on every row.</li>
                <li><b>The premium shown is what the producer pays.</b> The rate file's cost/cwt is the <i>gross</i> premium; the producer pays
                  gross × the RMA subsidy share for their coverage level.</li>
                <li><b>An endorsement is only listed if the cattle are market-ready inside RMA's 60-day marketing window.</b> Selling more than
                  60 days before the end date voids the endorsement outright, so a date the cattle cannot meet is not an alternative — it is
                  excluded, with its reason.</li>
                <li><b>Three cost scenarios, and the signal is judged on the conservative one.</b> Cost of gain is anchored on the measured
                  ISU-to-commercial-closeout range; the non-feed lines (yardage, interest, death loss, health, freight, commission) are
                  extension-budget typical values and are <b>not yet validated against this book's closeouts</b>.</li>
                <li><b>Coverage levels are compared on net protected economics</b>, never on the highest coverage price — the highest floor is
                  also the most expensive one.</li>
              </ul>
              <p style={{ margin: 0, color: "#8a6a2a" }}><b>Known limits.</b> Basis is measured from 11 matched observations of the realised
                index against NY <i>feeder</i> auction cash, not from a producer-specific basis. <b>Fed-cattle basis has not been measured</b> — the fed segment borrows the feeder figures and says so. The cost assumptions above are standardized. Historical
                validation of the cost model against real closeouts is not complete, which is why alerts remain off and this page is marked shadow.</p>
            </div>
          )}
        </>
      )}
      {!loading && !error && !anySeg && rows.length > 0 && (
        <div style={{ color: MUTED, padding: 20 }}>The snapshot holds no segment this page can render.</div>
      )}
    </div>
  );
}
